import { useCallback, useEffect, useState } from 'react';
import buildMeta from '../buildMeta.json';

const VERSION_CHECK_INTERVAL_MS = 240 * 60 * 1000;
const VERSION_CHECK_TIMEOUT_MS = 10000;
const isReactSnap = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';
const DISMISSED_BUILD_ID_STORAGE_KEY = 'app-update-dismissed-build-id';
const RELOAD_TARGET_BUILD_ID_STORAGE_KEY = 'app-update-reload-target-build-id';

const getSessionStorageValue = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const setSessionStorageValue = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // Ignore storage errors in private mode or restricted environments.
  }
};

const removeSessionStorageValue = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Ignore storage errors in private mode or restricted environments.
  }
};

const clearRuntimeCaches = async () => {
  const cleanupTasks = [];

  if ('serviceWorker' in navigator) {
    cleanupTasks.push(
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .catch(() => undefined)
    );
  }

  if ('caches' in window) {
    cleanupTasks.push(
      caches.keys()
        .then((cacheKeys) => Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey))))
        .catch(() => undefined)
    );
  }

  await Promise.all(cleanupTasks);
};

const fetchLatestBuildMeta = async () => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);

  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const useAppVersionRefresh = () => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [latestBuildId, setLatestBuildId] = useState(null);

  const checkVersion = useCallback(async () => {
    if (isReactSnap) {
      return;
    }

    const latestBuildMeta = await fetchLatestBuildMeta();
    if (!latestBuildMeta?.buildId) {
      return;
    }

    const nextBuildId = latestBuildMeta.buildId;

    if (nextBuildId === buildMeta.buildId) {
      removeSessionStorageValue(DISMISSED_BUILD_ID_STORAGE_KEY);
      removeSessionStorageValue(RELOAD_TARGET_BUILD_ID_STORAGE_KEY);
      setLatestBuildId(null);
      setIsUpdateAvailable(false);
      return;
    }

    const dismissedBuildId = getSessionStorageValue(DISMISSED_BUILD_ID_STORAGE_KEY);
    const reloadTargetBuildId = getSessionStorageValue(RELOAD_TARGET_BUILD_ID_STORAGE_KEY);

    setLatestBuildId(nextBuildId);

    if (dismissedBuildId === nextBuildId || reloadTargetBuildId === nextBuildId) {
      setIsUpdateAvailable(false);
      return;
    }

    setIsUpdateAvailable(true);
  }, []);

  const reloadApp = useCallback(async () => {
    const targetBuildId = latestBuildId;

    if (targetBuildId) {
      setSessionStorageValue(RELOAD_TARGET_BUILD_ID_STORAGE_KEY, targetBuildId);
      removeSessionStorageValue(DISMISSED_BUILD_ID_STORAGE_KEY);
    }

    setIsUpdateAvailable(false);

    await clearRuntimeCaches();

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('__reloadBuildId', targetBuildId || Date.now().toString());
    nextUrl.searchParams.set('__reloadTs', Date.now().toString());

    window.location.replace(nextUrl.toString());
  }, [latestBuildId]);

  const dismissUpdate = useCallback(() => {
    if (latestBuildId) {
      setSessionStorageValue(DISMISSED_BUILD_ID_STORAGE_KEY, latestBuildId);
    }

    setIsUpdateAvailable(false);
  }, [latestBuildId]);

  useEffect(() => {
    if (isReactSnap) {
      return undefined;
    }

    const currentReloadTarget = getSessionStorageValue(RELOAD_TARGET_BUILD_ID_STORAGE_KEY);
    if (currentReloadTarget === buildMeta.buildId) {
      removeSessionStorageValue(RELOAD_TARGET_BUILD_ID_STORAGE_KEY);
    }

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    void checkVersion();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [checkVersion]);

  return {
    isUpdateAvailable,
    reloadApp,
    dismissUpdate,
  };
};
